import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Question } from '@/types';
import { useGame } from '@/context/GameContext';
import XPTicker from '@/components/game/XPTicker';
import FillBlankQuestion from '@/components/question/FillBlankQuestion';
import DragDropQuestion from '@/components/question/DragDropQuestion';
import BossQuestion from '@/components/question/BossQuestion';
import CorrectFeedback from '@/components/question/CorrectFeedback';
import WrongFeedback from '@/components/question/WrongFeedback';
import { cn } from '@/lib/utils';

interface QuestionBlockProps {
    question: Question;
    onAnswered: (result: any) => void;
}

function getModeLabel(question: Question) {
    if (question.type === 'fill-blank') return 'KEY-IN RESPONSE';
    if (question.type === 'drag-drop') return 'SEQUENCE SORT';
    return 'TACTICAL CHOICE';
}

function getDifficultyTone(difficulty: Question['difficulty']) {
    if (difficulty === 'easy') return 'border-[#00cffc]/40 text-[#00cffc]';
    if (difficulty === 'medium') return 'border-[#8cacff]/40 text-[#8cacff]';
    return 'border-[#ff716c]/40 text-[#ff716c]';
}

export default function QuestionBlock({ question, onAnswered }: QuestionBlockProps) {
    const { submitAnswer, stats } = useGame();
    const [selected, setSelected] = useState<number | string | null>(null);
    const [fillValue, setFillValue] = useState('');
    const [result, setResult] = useState<any | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showEdgeFlash, setShowEdgeFlash] = useState<'correct' | 'wrong' | null>(null);
    const [showXPTicker, setShowXPTicker] = useState(false);
    const [tickerXP, setTickerXP] = useState(0);
    const startTime = useRef(Date.now());
    const controls = useAnimation();

    const answered = result !== null;
    const isMCQ = question.type === 'mcq';
    const isFillBlank = question.type === 'fill-blank';
    const isDragDrop = question.type === 'drag-drop';

    useEffect(() => {
        setSelected(null);
        setFillValue('');
        setResult(null);
        setSubmitting(false);
        setShowEdgeFlash(null);
        setShowXPTicker(false);
        setTickerXP(0);
        startTime.current = Date.now();
        controls.start({ opacity: 1, y: 0, x: 0 });
    }, [controls, question.id]);

    const processResult = useCallback(
        async (answer: number | string | string[]) => {
            if (answered || submitting) return;
            setSubmitting(true);

            const timeTaken = Math.round((Date.now() - startTime.current) / 1000);
            const res = await submitAnswer(question.id, answer, timeTaken);

            setResult(res);
            setSubmitting(false);

            if (res.correct) {
                setShowEdgeFlash('correct');
                setTickerXP(res.xp_awarded || question.xp_reward);
                setShowXPTicker(true);
                confetti({
                    particleCount: 60,
                    spread: 70,
                    origin: { y: 0.58 },
                    colors: ['#8cacff', '#00cffc', '#d674ff'],
                });
                setTimeout(() => setShowXPTicker(false), 1200);
            } else {
                setShowEdgeFlash('wrong');
                void controls.start({
                    x: [0, -10, 10, -10, 10, 0],
                    transition: { duration: 0.4 },
                });
            }

            setTimeout(() => setShowEdgeFlash(null), 300);
            setTimeout(() => onAnswered(res), 2000);
        },
        [answered, controls, onAnswered, question.id, question.xp_reward, submitAnswer, submitting],
    );

    const handleMCQSelect = (idx: number) => {
        if (answered || submitting) return;
        setSelected(idx);
        void processResult(idx);
    };

    const handleFillSubmit = () => {
        const val = fillValue.trim();
        if (!val || answered || submitting) return;
        setSelected(val);
        void processResult(val);
    };

    const handleDragSubmit = (order: string[]) => {
        if (answered || submitting) return;
        void processResult(order);
    };

    const correctIndex = typeof question.answer === 'number' ? question.answer : null;
    const livesRemaining = result?.lives_remaining ?? stats?.lives ?? 0;

    return (
        <>
            <AnimatePresence>
                {showEdgeFlash && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className={cn(
                            'pointer-events-none fixed inset-0 z-[9999] border-[10px]',
                            showEdgeFlash === 'correct' ? 'border-[#00cffc]/40' : 'border-[#ff716c]/40',
                        )}
                    />
                )}
            </AnimatePresence>

            <motion.div
                layout
                animate={controls}
                initial={{ opacity: 0, y: 24 }}
                className="relative overflow-hidden border border-[#2b2b2b] bg-[#101010] shadow-[0_0_0_1px_rgba(140,172,255,0.04)]"
            >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(140,172,255,0.08),transparent_55%)]" />
                <div className="absolute inset-y-0 left-[10%] w-px bg-[#8cacff]/6" />
                <div className="absolute inset-y-0 right-[14%] w-px bg-[#00cffc]/6" />

                <XPTicker xp={tickerXP} show={showXPTicker} />

                <div className="relative border-b border-[#202020] px-6 py-5 md:px-8">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="border border-[#8cacff]/40 bg-[#151d2f] px-3 py-1 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#8cacff]">
                                    ARENA_SYNC
                                </span>
                                <span className="border border-[#202020] bg-[#151515] px-3 py-1 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em] text-[#767575]">
                                    NODE // {question.order_index + 1}
                                </span>
                                <span className={cn("border px-3 py-1 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.28em]", getDifficultyTone(question.difficulty))}>
                                    {question.difficulty}
                                </span>
                                {question.is_boss && <BossQuestion />}
                            </div>
                            <h2 className="mt-6 max-w-4xl font-['Space_Grotesk'] text-3xl font-black uppercase italic leading-tight text-white md:text-5xl">
                                {question.prompt}
                            </h2>
                        </div>

                        <div className="grid min-w-[240px] grid-cols-2 gap-3">
                            <div className="border border-[#242424] bg-[#151515] px-4 py-4">
                                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.24em] text-[#767575]">
                                    Mode
                                </p>
                                <p className="mt-2 font-['Space_Grotesk'] text-lg font-black uppercase text-white">
                                    {getModeLabel(question)}
                                </p>
                            </div>
                            <div className="border border-[#242424] bg-[#151515] px-4 py-4">
                                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.24em] text-[#767575]">
                                    Reward
                                </p>
                                <p className="mt-2 font-['Space_Grotesk'] text-lg font-black uppercase text-[#00cffc]">
                                    +{question.xp_reward} XP
                                </p>
                            </div>
                            <div className="border border-[#242424] bg-[#151515] px-4 py-4">
                                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.24em] text-[#767575]">
                                    Life Pool
                                </p>
                                <p className="mt-2 font-['Space_Grotesk'] text-lg font-black uppercase text-[#ff716c]">
                                    {String(stats?.lives ?? 0).padStart(2, '0')}
                                </p>
                            </div>
                            <div className="border border-[#242424] bg-[#151515] px-4 py-4">
                                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.24em] text-[#767575]">
                                    Arena ID
                                </p>
                                <p className="mt-2 font-['IBM_Plex_Mono'] text-sm uppercase tracking-[0.22em] text-[#8cacff]">
                                    {question.id}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative px-6 py-8 md:px-8 md:py-10">
                    {question.hint && !answered ? (
                        <div className="mb-8 border border-[#2f3859] bg-[#171d2e] px-5 py-4">
                            <p className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-[#8cacff]">
                                Tactical Hint
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-[#cfd8ff]">{question.hint}</p>
                        </div>
                    ) : null}

                    {isMCQ && (
                        <div className="grid gap-4 lg:grid-cols-2">
                            {question.options?.map((opt, idx) => {
                                const isSelected = selected === idx;
                                const isCorrect = answered && correctIndex === idx;
                                const isWrong = answered && isSelected && !result?.correct;

                                return (
                                    <motion.button
                                        key={idx}
                                        whileHover={!answered ? { x: 4 } : {}}
                                        whileTap={!answered ? { scale: 0.985 } : {}}
                                        disabled={answered || submitting}
                                        onClick={() => handleMCQSelect(idx)}
                                        className={cn(
                                            'group flex min-h-[106px] items-start gap-4 border px-5 py-5 text-left transition-colors',
                                            answered
                                                ? isCorrect
                                                    ? 'border-[#00cffc] bg-[#00171b]'
                                                    : isWrong
                                                        ? 'border-[#ff716c] bg-[#221414]'
                                                        : 'border-[#242424] bg-[#151515]'
                                                : 'border-[#242424] bg-[#151515] hover:border-[#8cacff]/45 hover:bg-[#181c28]',
                                        )}
                                    >
                                        <span
                                            className={cn(
                                                "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center border font-['Space_Grotesk'] text-lg font-black uppercase",
                                                isCorrect
                                                    ? 'border-[#00cffc] bg-[#00cffc] text-[#071014]'
                                                    : isWrong
                                                        ? 'border-[#ff716c] bg-[#ff716c] text-[#180908]'
                                                        : isSelected
                                                            ? 'border-[#8cacff] bg-[#8cacff]/15 text-[#8cacff]'
                                                            : 'border-[#2b2b2b] bg-[#111111] text-[#8cacff]',
                                            )}
                                        >
                                            {String.fromCharCode(65 + idx)}
                                        </span>
                                        <div className="flex-1">
                                            <p className="font-['Space_Grotesk'] text-xl font-bold uppercase leading-snug text-white">
                                                {opt}
                                            </p>
                                            <p className="mt-3 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#767575]">
                                                {answered
                                                    ? isCorrect
                                                        ? 'Validated'
                                                        : isWrong
                                                            ? 'Rejected'
                                                            : 'Unchecked'
                                                    : 'Awaiting input'}
                                            </p>
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </div>
                    )}

                    {isFillBlank && (
                        <FillBlankQuestion
                            value={fillValue}
                            onChange={setFillValue}
                            onSubmit={handleFillSubmit}
                            answered={answered}
                            submitting={submitting}
                            isCorrect={!!result?.correct}
                            answer={question.answer}
                        />
                    )}

                    {isDragDrop && (
                        <DragDropQuestion
                            options={question.options ?? []}
                            answered={answered}
                            submitting={submitting}
                            resultCorrect={!!result?.correct}
                            onSubmit={handleDragSubmit}
                        />
                    )}
                </div>

                <AnimatePresence>
                    {answered && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 z-20 flex items-center justify-center bg-[#080808]/78 px-4 py-8 backdrop-blur-[2px]"
                        >
                            <motion.div
                                initial={{ y: 18, opacity: 0, scale: 0.98 }}
                                animate={{ y: 0, opacity: 1, scale: 1 }}
                                exit={{ y: 10, opacity: 0, scale: 0.98 }}
                                className="w-full max-w-2xl"
                            >
                                {result.correct ? (
                                    <CorrectFeedback
                                        explanation={result.explanation || question.explanation}
                                        xpAwarded={result.xp_awarded || question.xp_reward}
                                        livesRemaining={livesRemaining}
                                        streakDays={stats?.streak_days ?? 0}
                                    />
                                ) : (
                                    <WrongFeedback
                                        explanation={result.explanation || question.explanation}
                                        answer={question.answer}
                                        livesRemaining={livesRemaining}
                                    />
                                )}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </>
    );
}
